import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TextField,
  Typography,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material';
import { PrimaryButton } from '../../../utils/common/styles/commonStyles';
import { setDriveVideoURL } from '../store/drive.actions';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';

const SidebarContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  marginTop: 5,
  height: '100%',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
  position: 'relative',
  boxSizing: 'border-box',
}));

const DriveSidebar = ({
  drives,
  viewAll,
  isLoading,
  search,
  setSearch,
  navigate,
  setSelectedDrive,
  selectedDrive,
  onManageCollection,
}) => {
  const filteredDrives = drives.filter((d) => d.name?.toLowerCase().includes(search.toLowerCase()));

  const dispatch = useDispatch();
  const itemRefs = useRef({});

  useEffect(() => {
    if (!selectedDrive?.id) return;

    const selectedItem = itemRefs.current[selectedDrive.id];
    if (selectedItem) {
      selectedItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedDrive, filteredDrives]);

  const driveListContent = isLoading ? (
    <Stack
      spacing={1}
      sx={{
        p: 2,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {[...Array(5)].map((_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          height={60}
          sx={{
            width: '100%',
          }}
        />
      ))}
    </Stack>
  ) : (
    <>
      {!selectedDrive && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'rgba(255,255,255,0.9)',
            mx: 1,
            mb: 1,
            width: 'calc(100% - 16px)',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Click a drive in the list to view details
          </Typography>
        </Paper>
      )}

      {filteredDrives.length > 0 ? (
        filteredDrives.map((d) => (
          <ListItemButton
            key={d.id}
            ref={(el) => {
              if (el) {
                itemRefs.current[d.id] = el;
              } else {
                delete itemRefs.current[d.id];
              }
            }}
            onClick={() => {
              if (selectedDrive?.id === d.id) {
                setSelectedDrive(null);
                localStorage.removeItem('selectedDriveRow');
                dispatch(setDriveVideoURL(null));
                return;
              }

              setSelectedDrive(d);
              localStorage.setItem('selectedDriveRow', JSON.stringify(d));
              dispatch(setDriveVideoURL(d.video_url || null));
            }}
            selected={selectedDrive?.id === d.id}
            sx={{
              borderRadius: 2,
              mb: 1,
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              padding: '8px 12px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderLeft: '5px solid rgba(102, 126, 234, 0.8)',
              overflow: 'hidden',

              '&.Mui-selected': {
                border: '4px solid #667eea',
                padding: '5px 9px',
              },

              '&.MuiButtonBase-root.MuiListItemButton-root:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },

              '&:last-child': {
                mb: 0,
              },
            }}
          >
            <ListItemText
              primary={d.name || 'Unnamed Drive'}
              secondary={d.description || ''}
              sx={{
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                borderRadius: 2,

                '& .MuiListItemText-primary': {
                  display: 'block',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  lineHeight: 1.4,
                },

                '& .MuiListItemText-secondary': {
                  display: 'block',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  mt: 0.25,
                },
              }}
            />
          </ListItemButton>
        ))
      ) : (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
            }}
          >
            No files available.
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <SidebarContainer>
      {/* Search Header */}
      <Box
        sx={{
          p: 1.5,
          pb: 1,
          flexShrink: 0,
          backgroundColor: 'background.paper',
        }}
      >
        <TextField
          placeholder="Search drives..."
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      {/* Scrollable List Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1.5,
          py: 0.5,
        }}
      >
        {driveListContent}
      </Box>

      {/* Fixed Footer Button */}
      {!viewAll && (
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <PrimaryButton
            variant="contained"
            onClick={onManageCollection}
            sx={{
              borderRadius: '8px',
              width: '100%',
              py: 1,
            }}
          >
            Manage Collection
          </PrimaryButton>
        </Box>
      )}
    </SidebarContainer>
  );
};

DriveSidebar.propTypes = {
  drives: PropTypes.array.isRequired,
  viewAll: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  setSelectedDrive: PropTypes.func.isRequired,
  selectedDrive: PropTypes.object,
  onManageCollection: PropTypes.func,
};

DriveSidebar.defaultProps = {
  isLoading: false,
  onManageCollection: () => {},
};

export default DriveSidebar;
